import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
    signInWithEmailAndPassword,
    signInWithPopup,
    GoogleAuthProvider,
    sendPasswordResetEmail,
} from "firebase/auth";
import { auth } from "../lib/firebase";
import { Toast } from "./ui/Toast";

function getFirebaseError(code) {
    console.error("Firebase auth error code:", code);
    switch (code) {
        case "auth/user-not-found":
        case "auth/wrong-password":
        case "auth/invalid-credential":    return "Invalid email or password.";
        case "auth/too-many-requests":     return "Too many attempts. Try again later.";
        case "auth/invalid-email":         return "Invalid email address.";
        case "auth/popup-closed-by-user":  return "Google sign-in was cancelled.";
        case "auth/operation-not-allowed": return "Email/password sign-in is not enabled. Check Firebase console.";
        case "auth/network-request-failed":return "Network error. Check your connection.";
        case "auth/user-disabled":         return "This account has been disabled.";
        default:                           return `Something went wrong (${code}). Please try again.`;
    }
}

function Login() {
    const navigate = useNavigate();
    const [view, setView] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [forgotEmail, setForgotEmail] = useState("");
    const [resetSent, setResetSent] = useState(false);
    const [toast, setToast] = useState({ show: false, title: "", message: "" });

    function showToast(title, message) {
        setToast({ show: true, title, message });
    }

    async function handleSignIn(e) {
        e.preventDefault();
        try {
            await signInWithEmailAndPassword(auth, email, password);
            navigate("/");
        } catch (err) {
            showToast("Sign in failed", getFirebaseError(err.code));
        }
    }

    async function handleGoogleSignIn() {
        try {
            const provider = new GoogleAuthProvider();
            provider.setCustomParameters({ prompt: "select_account" });
            await signInWithPopup(auth, provider);
            navigate("/");
        } catch (err) {
            showToast("Google sign in failed", getFirebaseError(err.code));
        }
    }

    async function handleForgotPassword(e) {
        e.preventDefault();
        try {
            await sendPasswordResetEmail(auth, forgotEmail);
            setResetSent(true);
        } catch (err) {
            showToast("Error", getFirebaseError(err.code));
        }
    }

    if (view === "forgot") {
        return (
            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                {toast.show && (
                    <Toast
                        title={toast.title}
                        message={toast.message}
                        onClose={() => setToast(t => ({ ...t, show: false }))}
                    />
                )}
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-white-900">
                        Reset your password
                    </h2>
                </div>
                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
                    {resetSent ? (
                        <div className="text-center space-y-4">
                            <p className="text-gray-400">
                                Reset link sent to <span className="text-white">{forgotEmail}</span>. Check your inbox.
                            </p>
                            <button
                                className="text-sm text-gray-400 hover:text-white underline"
                                onClick={() => { setView("login"); setResetSent(false); setForgotEmail(""); }}
                            >
                                Back to sign in
                            </button>
                        </div>
                    ) : (
                        <form className="space-y-6" onSubmit={handleForgotPassword}>
                            <div>
                                <label htmlFor="forgot-email" className="block text-sm font-medium leading-6 text-white-900">
                                    Email address
                                </label>
                                <div className="mt-2">
                                    <input
                                        id="forgot-email"
                                        type="email"
                                        value={forgotEmail}
                                        onChange={e => setForgotEmail(e.target.value)}
                                        required
                                        className="block w-full rounded-md border-0 px-3 py-2.5 text-white-900 shadow-sm ring-1 ring-inset ring-white-300 placeholder:text-white-400 focus:ring-2 focus:ring-inset focus:ring-white-600 sm:text-sm sm:leading-6"
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                className="flex w-full justify-center rounded-md bg-gray-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-600"
                            >
                                Send reset email
                            </button>
                            <button
                                type="button"
                                className="w-full text-center text-sm text-gray-400 hover:text-white"
                                onClick={() => setView("login")}
                            >
                                Back to sign in
                            </button>
                        </form>
                    )}
                </div>
                <p className="mt-10 text-center text-sm text-gray-500">
                    <Link to="/" className="font-semibold text-gray-400 hover:text-white">
                        ← Back to home
                    </Link>
                </p>
            </div>
        );
    }

    return (
        <>
            {toast.show && (
                <Toast
                    title={toast.title}
                    message={toast.message}
                    onClose={() => setToast(t => ({ ...t, show: false }))}
                />
            )}
            <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
                <div className="sm:mx-auto sm:w-full sm:max-w-sm">
                    <h2 className="mt-10 text-center text-2xl font-bold leading-9 tracking-tight text-white-900">
                        Sign in to your account
                    </h2>
                </div>

                <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm space-y-6">
                    <form className="space-y-6" onSubmit={handleSignIn}>
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium leading-6 text-white-900">
                                Email address
                            </label>
                            <div className="mt-2">
                                <input
                                    id="email"
                                    type="email"
                                    autoComplete="email"
                                    value={email}
                                    onChange={e => setEmail(e.target.value)}
                                    required
                                    className="block w-full rounded-md border-0 px-3 py-2.5 text-white-900 shadow-sm ring-1 ring-inset ring-white-300 placeholder:text-white-400 focus:ring-2 focus:ring-inset focus:ring-white-600 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="block text-sm font-medium leading-6 text-white-900">
                                    Password
                                </label>
                                <button
                                    type="button"
                                    className="text-sm font-semibold text-gray-400 hover:text-gray-300"
                                    onClick={() => setView("forgot")}
                                >
                                    Forgot password?
                                </button>
                            </div>
                            <div className="mt-2">
                                <input
                                    id="password"
                                    type="password"
                                    autoComplete="current-password"
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    required
                                    className="block w-full rounded-md border-0 px-3 py-2.5 text-white-900 shadow-sm ring-1 ring-inset ring-white-300 placeholder:text-white-400 focus:ring-2 focus:ring-inset focus:ring-gray-500 sm:text-sm sm:leading-6"
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            className="flex w-full justify-center rounded-md bg-gray-700 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-500"
                        >
                            Sign in
                        </button>
                    </form>

                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700" />
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="bg-black px-2 text-gray-400">Or continue with</span>
                        </div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogleSignIn}
                        className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-700 bg-transparent px-3 py-2.5 text-sm font-semibold text-white hover:bg-gray-900 transition-colors"
                    >
                        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                        </svg>
                        Continue with Google
                    </button>

                    <p className="text-center text-sm text-gray-500">
                        <Link to="/" className="font-semibold text-gray-400 hover:text-white">
                            ← Back to home
                        </Link>
                    </p>
                </div>
            </div>
        </>
    );
}

export default Login;
